/**
 * Document Designer — authors a Document Repository (obj_type "K").
 *
 * The one thing to understand before changing anything here: **the model file
 * is a SEED, not the truth.** Document types and their metadata fields live in
 * the tenant database so a runtime administrator can add one without a
 * redeploy. What this designer edits is the seed; a redeploy reconciles the
 * `design` rows from it and leaves `runtime` rows exactly as the administrator
 * left them. The Types tab shows both, and marks which is which, because
 * editing a runtime-authored type here would do nothing and that would be a
 * confusing surprise.
 *
 * Tabs mirror the decisions an owner actually makes:
 *   Repository — storage backend, viewer policy, AI posture
 *   Types      — document types, their metadata fields, retention and review
 *   Rights     — who may view / add / edit / download / print / feedback /
 *                share / delete, at repository, business-area, folder, type or
 *                document scope
 *   Operations — provider + scanner health, the ingestion queue, bulk import
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import {
  bulkImport, drainJobs, listAcl, listJobs, listTypes, loadRepository,
  reindexPending, reindexRepository, reindexStatus,
  providerStatus, retryJob, saveAcl, saveRepository, saveType,
  type DmsProviderStatus, type DmsReindexStatus,
} from '@/api/dms'
import { getVectorSettings, type VectorProfileOption } from '@/api/vectorSearch'
import {
  FactChip, ReindexConfirm, StatusChip, TabButton,
  btnPrimaryStyle, btnStyle as dmsBtnStyle, cardStyle as dmsCardStyle,
  headerStyle as dmsHeaderStyle, inputStyle as dmsInputStyle,
  labelStyle as dmsLabelStyle, RADIUS, T, TYPE,
  type DmsDocType,
} from '@twasta/page-runtime/dms'
import DefaultValuePicker, {
  validateTokenDefault, isPrefixedSeriesToken,
} from '@/components/designers/DefaultValuePicker'
import SeriesEditor, { seriesSummary } from '../transaction/SeriesEditor'
// The transaction designer's own editors, reused verbatim. A repository field
// carries the same `lookup` / `display` / `validations` / `item_change` shapes
// and the engine runs them with the same code, so authoring them through a
// second set of editors would only be a way for the two to drift.
import EditMaskEditor from '../transaction/EditMaskEditor'
import LookupEditor from '../transaction/LookupEditor'
import DisplayFormatEditor from '../transaction/DisplayFormatEditor'
import ValidationRuleEditor from '../transaction/ValidationRuleEditor'
import ItemChangeRuleEditor from '../transaction/ItemChangeRuleEditor'
import ItemChangeCodeEditor from '../transaction/ItemChangeCodeEditor'
import {
  type ProjectVariable, listProjectVariables,
} from '@/api/projectVariables'
import type { EmbeddedDesignerProps } from '../embeddedDesigner'
import { TemplatesTab } from './TemplatesTab'
import { SignaturesTab } from './SignaturesTab'

type Tab = 'repository' | 'structure' | 'types' | 'templates' | 'signatures'
  | 'rights' | 'operations'

/**
 * What each tab is CALLED, as opposed to what it is keyed on.
 *
 * The labels used to be the keys with a capital letter — "Types", "Rights",
 * "Operations" — which named the data model rather than the job. An
 * administrator opening this for the first time should be able to tell from
 * the tab strip what is left to set up.
 */
const TAB_LABELS: Record<Tab, string> = {
  repository: 'Name and purpose',
  structure: 'Folders',
  types: 'Kinds of document',
  templates: 'Templates',
  signatures: 'Signatures',
  rights: 'Who can do what',
  operations: 'Health',
}

// Mirrors `store.RIGHTS`. `add` (file a NEW document) is separate from `edit`
// (change one that exists), and `feedback` (comment on a passage) is separate
// from both — a reviewer who may say a procedure is wrong is usually not
// somebody who may rewrite it.
const RIGHTS = ['view', 'add', 'edit', 'download', 'print', 'feedback', 'sign',
                'share', 'delete', 'reindex'] as const

/** What each right is CALLED. The slug is the data; this is the English. */
const RIGHT_LABELS: Record<string, string> = {
  view: 'Find & read',
  add: 'Add new',
  edit: 'Revise',
  download: 'Download',
  print: 'Print',
  feedback: 'Comment',
  sign: 'Approve',
  share: 'Share',
  delete: 'Delete',
  reindex: 'Rebuild search',
}

const RIGHT_HELP: Record<string, string> = {
  view: 'Find it in the library and read it on screen.',
  add: 'File something new, and create folders.',
  edit: 'Change an existing one — write a new version, fill in details, move '
      + 'it along.',
  download: 'Save a copy of the original file, or take it away.',
  print: 'Print it. Every page carries the reader\u2019s name.',
  feedback: 'Comment on a passage and suggest a re-write.',
  sign: 'Approve or send back. Deliberately separate from Revise — an '
      + 'approver signs off what they are not allowed to change.',
  share: 'Share it with someone else. NOT YET ENFORCED — there is no sharing '
       + 'feature behind this tick today, so it grants and withholds nothing. '
       + 'Set it for the intent; it will take effect when sharing ships.',
  delete: 'Remove it from the library.',
  reindex: 'Have every document read again so search and answers are up to '
      + 'date. Costs money with your AI provider, so it is granted by name — '
      + 'administrators have it anyway.',
}

/**
 * What a row of ticks actually means, written out.
 *
 * A matrix answers "which boxes are ticked"; it does not answer "so what can
 * this person do?" — which is the only question anybody setting up a library
 * is actually asking. The two most consequential absences get said out loud,
 * because they are the ones that surprise people: read-but-not-download, and
 * a role that cannot see anything at all.
 */
function plainRights(entry: Record<string, unknown>): string[] {
  const has = (right: string) => Boolean(entry[right])
  if (!has('view')) {
    return ['Cannot see anything in this library. Documents they are not '
            + 'entitled to never appear for them at all — not even as a '
            + 'locked row.']
  }
  const lines = ['Can find documents here and read them on screen.']
  if (!has('download') && !has('print')) {
    lines.push('Cannot save a copy or print one — reading on screen only.')
  } else {
    const both: string[] = []
    if (has('download')) both.push('download')
    if (has('print')) both.push('print')
    lines.push(`Can ${both.join(' and ')} what they can read.`)
  }
  if (has('add') || has('edit')) {
    lines.push(has('add') && has('edit')
      ? 'Can file new documents and write new versions of existing ones.'
      : has('add') ? 'Can file new documents, but not change existing ones.'
        : 'Can write new versions, but not file anything new.')
  } else {
    lines.push('Cannot add or change anything.')
  }
  if (has('sign')) {
    lines.push('Can approve a document, or send it back — even ones they are '
               + 'not allowed to change.')
  }
  if (has('delete')) {
    lines.push('Can remove documents from the library.')
  }
  return lines
}

// Grants are made at one of these. Most specific wins: a document beats a type,
// which beats a folder, which beats a business area, which beats the whole
// repository.
const SCOPES = [
  { value: 'repo', label: 'Whole repository', needsId: false,
    help: 'Everything in this repository.' },
  { value: 'business_area', label: 'Business area', needsId: true,
    help: 'Every document whose business-area metadata field holds this VALUE — wherever it is filed. Set the field on the Repository tab first.' },
  { value: 'folder', label: 'Folder', needsId: true,
    help: 'A folder and everything beneath it. Enter the folder id.' },
  { value: 'doctype', label: 'One kind of document', needsId: true,
    help: 'Every document of one kind. Enter its id.' },
  { value: 'document', label: 'One document', needsId: true,
    help: 'A single document. Use sparingly — per-document rows are what make a large repository unauditable.' },
] as const

/**
 * The three answers to "where does a document of this type come from".
 *
 * `required` is the one that carries weight: it also refuses UPLOADS, so a
 * repository cannot end up holding two kinds of "the policy" — one written here
 * and one filed from a laptop, only one of which can be opened, compared or
 * revised.
 */
const AUTHORING_MODES = [
  { value: 'off', label: 'Uploaded only',
    help: 'These arrive as files people already have — PDF, Word, scans.' },
  { value: 'optional', label: 'Written here or uploaded',
    help: 'Both are offered. Use it while a library is being moved over, or '
      + 'where some are written here and others are scanned originals.' },
  { value: 'required', label: 'Written here only',
    help: 'These are written in the built-in editor and cannot be uploaded. '
      + 'Every version then has a source, so any two can be compared and any '
      + 'of them revised.' },
]

const AI_MODES: { value: string; label: string; help: string }[] = [
  { value: 'full', label: 'Yes, and it may quote them',
    help: 'Readers get answers with the exact wording and a link to the page.' },
  { value: 'redacted', label: 'Yes, but names and numbers are hidden first',
    help: 'Use for anything with personal or commercial detail in it.' },
  { value: 'summary_only', label: 'Only a short summary',
    help: 'A summary is kept for answering; the body is never sent anywhere.' },
  { value: 'none', label: 'No, never',
    help: 'The text never leaves this server.' },
]

const FLAGS: { key: string; label: string; help: string }[] = [
  { key: 'metadata_search', label: 'Can be found by name',
    help: 'Off hides these from the library and from search entirely.' },
  { key: 'full_text_search', label: 'The words inside can be searched',
    help: 'Off means only the name is searchable, never the contents.' },
  { key: 'ai_qa', label: 'The assistant can answer from these',
    help: 'Off means these are never used to answer a question.' },
]

/**
 * What is still unfinished, in the order it will bite.
 *
 * One of these is not a matter of taste. A transition marked `"via":
 * "approval"` resolves its approvers from the transition, then the kind of
 * document, then the repository — and if none of the three defines any, the
 * transition RAISES at runtime. Until now nothing said so here, so the first
 * anybody heard of it was an author being refused when they submitted a real
 * document. It is a setup mistake, and it belongs on the setup screen.
 */
function setupProblems(repository: Record<string, any>): string[] {
  const problems: string[] = []
  const transitions: any[] = repository?.lifecycle?.transitions || []
  const needsApproval = transitions.filter((t) => t?.via === 'approval')
  if (needsApproval.length) {
    const repoLevels: any[] = repository?.approval?.levels || []
    const types: any[] = repository?.document_types || []
    const uncovered = needsApproval.some((t) => {
      if ((t?.approval?.levels || []).length) return false
      if (repoLevels.length) return false
      // A kind of its own can carry the levels — but only if EVERY kind does,
      // since the transition applies to all of them.
      return !types.length || types.some((k) => !(k?.approval?.levels || []).length)
    })
    if (uncovered) {
      problems.push(
        `${needsApproval.length === 1 ? 'A step' : `${needsApproval.length} steps`} `
        + `(${needsApproval.map((t) => t.action).join(', ')}) `
        + 'needs approval, but nobody has been named as an approver. Documents '
        + 'will be refused when someone tries to move them along. Set the '
        + 'approvers on this screen, or on the kind of document.')
    }
  }
  if (!(repository?.document_types || []).length) {
    problems.push('There are no kinds of document yet, so nothing can be filed here.')
  }
  return problems
}

export default function DocumentDesignerPage({
  project, objName, onDirtyChange, registerSave, onSaved,
}: { project: string; objName: string } & EmbeddedDesignerProps) {
  const [tab, setTab] = useState<Tab>('repository')
  const [model, setModel] = useState<Record<string, any> | null>(null)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setError('')
    try {
      const res = await loadRepository(project, objName)
      setModel((res.content || res.default || null) as Record<string, any> | null)
      setDirty(!res.content)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message
        : 'The repository could not be loaded.')
    }
  }, [project, objName])

  useEffect(() => { void load() }, [load])

  const repository = model?.repository as Record<string, any> | undefined

  const patch = useCallback((path: string[], value: unknown) => {
    setModel((prior) => {
      if (!prior) return prior
      const next = structuredClone(prior)
      let node: any = next
      for (const key of path.slice(0, -1)) {
        if (typeof node[key] !== 'object' || node[key] === null) node[key] = {}
        node = node[key]
      }
      node[path[path.length - 1]] = value
      return next
    })
    setDirty(true)
  }, [])

  const save = useCallback(async () => {
    if (!model) return false
    setSaving(true)
    try {
      await saveRepository(project, objName, model)
      setDirty(false)
      onSaved?.()
      toast.success('Repository saved')
      return true
    } catch (saveError) {
      toast.error(saveError instanceof Error ? saveError.message : 'Save failed')
      return false
    } finally {
      setSaving(false)
    }
  }, [model, project, objName, onSaved])

  // The workbench shell guards navigation on dirty state and drives its own
  // "Save & Continue" through the registered callback — same contract every
  // other embedded designer honours.
  useEffect(() => { onDirtyChange?.(dirty) }, [dirty, onDirtyChange])
  useEffect(() => { registerSave?.(save) }, [registerSave, save])

  if (error) return <div style={errorBoxStyle}>{error}</div>
  if (!model || !repository) return <div style={mutedBoxStyle}>Loading…</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <header style={headerStyle}>
        <strong style={{ fontSize: TYPE.heading, fontWeight: 600 }}>{repository.title || objName}</strong>
        <nav style={{ display: 'flex', gap: 4 }}>
          {(['repository', 'structure', 'types', 'templates', 'signatures',
            'rights', 'operations'] as Tab[]).map((name) => (
            <TabButton key={name} active={tab === name} onClick={() => setTab(name)}>
              {TAB_LABELS[name]}
            </TabButton>
          ))}
        </nav>
        <div style={{ flex: 1 }} />
        <button type="button" style={dirty ? btnPrimaryStyle : btnStyle}
                disabled={!dirty || saving} onClick={() => void save()}>
          {saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
        </button>
      </header>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16 }}>
        {(() => {
          const problems = setupProblems(repository)
          if (!problems.length) return null
          return (
            <div style={setupWarnStyle}>
              <strong style={{ display: 'block', marginBottom: 4 }}>
                {problems.length === 1
                  ? 'One thing is still unfinished'
                  : `${problems.length} things are still unfinished`}
              </strong>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {problems.map((problem) => (
                  <li key={problem} style={{ marginBottom: 3 }}>{problem}</li>
                ))}
              </ul>
            </div>
          )
        })()}
        {tab === 'repository' ? (
          <RepositoryTab project={project} repository={repository} patch={patch} />
        ) : null}
        {tab === 'structure' ? (
          <StructureTab repository={repository} patch={patch} />
        ) : null}
        {tab === 'types' ? (
          <TypesTab project={project} objName={objName} repository={repository} patch={patch} />
        ) : null}
        {tab === 'templates' ? (
          <TemplatesTab repository={repository} patch={patch}
                        doctypes={repository.document_types || []} />
        ) : null}
        {tab === 'signatures' ? (
          <SignaturesTab project={project} objName={objName}
                         repository={repository} patch={patch} />
        ) : null}
        {tab === 'rights' ? (
          <RightsTab project={project} objName={objName} repository={repository} patch={patch} />
        ) : null}
        {tab === 'operations' ? (
          <OperationsTab project={project} objName={objName} />
        ) : null}
      </div>
    </div>
  )
}

// ── Repository ─────────────────────────────────────────────────────────────

function RepositoryTab({ project, repository, patch }: {
  project: string
  repository: Record<string, any>
  patch: (path: string[], value: unknown) => void
}) {
  const storage = repository.storage || {}
  const viewer = repository.viewer || {}
  const ai = repository.ai || {}
  const config = storage.config || {}

  // The SAME embedding profiles master vector search offers — one list of
  // configs per owner, so a repository cannot name one that does not exist.
  const [profiles, setProfiles] = useState<VectorProfileOption[]>([])
  const [defaultProfile, setDefaultProfile] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    getVectorSettings(project)
      .then((res) => {
        if (!live) return
        setProfiles(res.profiles || [])
        setDefaultProfile(res.settings_default || null)
      })
      .catch(() => { /* the field stays typeable */ })
    return () => { live = false }
  }, [project])
  const chosenProfile = String(ai.embedding_profile || '')
  const profileKnown = !chosenProfile
    || profiles.some((p) => p.name === chosenProfile)

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 860 }}>
      <Group title="What this library is called"
             note="The title is what readers see at the top of the library.">
        <Field label="Title">
          <input style={inputStyle} value={repository.title || ''}
                 onChange={(e) => patch(['repository', 'title'], e.target.value)} />
        </Field>
        <Field label="Description">
          <textarea style={{ ...inputStyle, minHeight: 60 }} value={repository.description || ''}
                    onChange={(e) => patch(['repository', 'description'], e.target.value)} />
        </Field>
      </Group>

      <Group title="Storage"
             note="Files are always envelope-encrypted with this tenant's data key before they leave the server, on either backend. Moving from local disk to S3 is a configuration change, not a migration of format.">
        <Field label="Backend">
          <select style={inputStyle} value={storage.backend || 'local'}
                  onChange={(e) => patch(['repository', 'storage', 'backend'], e.target.value)}>
            <option value="local">Local disk</option>
            <option value="s3">S3 (or S3-compatible)</option>
            <option value="mayan">Mayan EDMS (external)</option>
            <option value="alfresco">Alfresco (external)</option>
          </select>
        </Field>
        {storage.backend === 's3' ? (
          <>
            <Field label="Bucket">
              <input style={inputStyle} value={config.bucket || ''}
                     onChange={(e) => patch(['repository', 'storage', 'config', 'bucket'], e.target.value)} />
            </Field>
            <Field label="Region">
              <input style={inputStyle} value={config.region || ''}
                     onChange={(e) => patch(['repository', 'storage', 'config', 'region'], e.target.value)} />
            </Field>
            <Field label="Endpoint URL" help="Leave blank for AWS; set it for MinIO, R2 or another compatible store.">
              <input style={inputStyle} value={config.endpoint_url || ''}
                     onChange={(e) => patch(['repository', 'storage', 'config', 'endpoint_url'], e.target.value)} />
            </Field>
          </>
        ) : null}
        {storage.backend === 'mayan' || storage.backend === 'alfresco' ? (
          <Field label="Server URL"
                 help="An external repository keeps its own version history and its own encryption. Twasta adds lifecycle, rights, the viewer and Q&A on top.">
            <input style={inputStyle} value={config.url || ''}
                   onChange={(e) => patch(['repository', 'storage', 'config', 'url'], e.target.value)} />
          </Field>
        ) : null}
      </Group>

      <Group title="Viewer"
             note="The viewer serves watermarked page images, never the original file. That is deterrence plus forensics — it does not, and does not claim to, prevent a screenshot or a photograph.">
        <Toggle label="Secure view" checked={viewer.secure_view !== false}
                onChange={(v) => patch(['repository', 'viewer', 'secure_view'], v)} />
        <Field label="Watermark"
               help="Tokens: {user_name} (the default — a watermark is read by a person, so it names one), {user_id} for the exact key, {user} as an alias of the name, plus {ip} {datetime} {date} {document} {tenant}">
          <input style={inputStyle} value={viewer.watermark || ''}
                 onChange={(e) => patch(['repository', 'viewer', 'watermark'], e.target.value)} />
        </Field>
        <Toggle label="Allow download (for holders of the download right)"
                checked={viewer.allow_download !== false}
                onChange={(v) => patch(['repository', 'viewer', 'allow_download'], v)} />
        <Toggle label="Allow print (for holders of the print right)"
                checked={Boolean(viewer.allow_print)}
                onChange={(v) => patch(['repository', 'viewer', 'allow_print'], v)} />
      </Group>

      <Group title="The assistant, and privacy"
             note="A metadata field marked sensitive is forced to 'Never' regardless of what its type says — the safe default is that sensitive values do not reach an embedding provider at all.">
        <Toggle label="Answer questions from these documents"
                checked={Boolean(ai.qa_enabled)}
                onChange={(v) => patch(['repository', 'ai', 'qa_enabled'], v)} />
        <Toggle label="Consider draft documents (search and question answering)"
                checked={ai.include_drafts !== false}
                onChange={(v) => patch(['repository', 'ai', 'include_drafts'], v)} />
        <p style={helpStyle}>
          One switch for both surfaces, so search and answers can never
          disagree about what exists. On, a draft the reader is entitled to
          see is findable and quotable — always labelled with its status. Off,
          both consider only approved or published documents.
        </p>
        <Toggle label="Suggest metadata automatically (a person still confirms it)"
                checked={Boolean(ai.extraction_enabled)}
                onChange={(v) => patch(['repository', 'ai', 'extraction_enabled'], v)} />
        <Field label="Embedding profile"
               help="A named AI profile from Settings → AI. Only the NAME is stored with the project — the provider, model and key are resolved from your own settings at run time. Point it at a self-hosted, OpenAI-compatible endpoint to keep document text on your own infrastructure.">
          <select
            style={inputStyle}
            value={profileKnown ? chosenProfile : '__custom__'}
            onChange={(e) => patch(
              ['repository', 'ai', 'embedding_profile'],
              e.target.value === '__custom__' ? chosenProfile : e.target.value,
            )}
          >
            <option value="">
              Use the default from Settings → AI
              {defaultProfile ? ` (${defaultProfile})` : ''}
            </option>
            {profiles.map((p) => (
              <option key={p.name} value={p.name}>
                {p.name} — {p.provider === 'openai' ? 'OpenAI' : 'Google'} · {p.model}
                {p.has_key ? '' : ' (no API key)'}
              </option>
            ))}
            {!profileKnown && (
              <option value="__custom__">{chosenProfile} (not in your settings)</option>
            )}
          </select>
          {profiles.length === 0 && (
            <p style={helpStyle}>
              No embedding-capable profile exists yet. Add an <strong>API</strong>{' '}
              configuration on OpenAI or Google in <strong>Settings → AI</strong>.
              Without one, documents are still indexed and still searchable by
              keyword — questions just cannot be answered by meaning.
            </p>
          )}
          {!profileKnown && (
            <p style={helpStyle}>
              <strong>{chosenProfile}</strong> is not one of your AI
              configurations. Retrieval falls back to keyword matching until it
              resolves — pick a profile above, or add that configuration.
            </p>
          )}
        </Field>
        <Toggle label="Remove identifiers before indexing"
                checked={ai.redaction?.enabled !== false}
                onChange={(v) => patch(['repository', 'ai', 'redaction', 'enabled'], v)} />
        <Toggle label="Also run a model pass over unstructured spans (slower, costs a call per document)"
                checked={Boolean(ai.redaction?.llm_pass)}
                onChange={(v) => patch(['repository', 'ai', 'redaction', 'llm_pass'], v)} />
        <Field label="Who may see hidden names and numbers"
               help="Comma-separated. EMPTY MEANS NOBODY — a redacted answer keeps its placeholders for everyone unless a role is named here.">
          <input style={inputStyle}
                 value={(ai.redaction?.reveal_roles || []).join(', ')}
                 onChange={(e) => patch(
                   ['repository', 'ai', 'redaction', 'reveal_roles'],
                   e.target.value.split(',').map((r: string) => r.trim().toLowerCase())
                     .filter(Boolean))} />
        </Field>
        <Toggle label="Encrypt the search index in the database"
                checked={Boolean(ai.encrypt_index)}
                onChange={(v) => patch(['repository', 'ai', 'encrypt_index'], v)} />
        <p style={helpStyle}>
          Document files are always encrypted at rest, and so is their extracted
          text. The search index is separate: by default its chunks are stored
          as readable text so keyword matching works without an embedding
          model. Switching this on encrypts them too — which means retrieval
          then <strong>requires</strong> a working embedding profile, and a
          repository without one stops answering rather than falling back. Turn
          it on when a database backup falling into the wrong hands is part of
          your threat model.
        </p>
      </Group>

      <Group title="Uploads">
        <Toggle label="Require a clean malware scan before review"
                checked={Boolean(repository.require_malware_scan)}
                onChange={(v) => patch(['repository', 'require_malware_scan'], v)} />
        <p style={helpStyle}>
          With this on and no scanner configured, documents are refused rather
          than passed through unchecked. Configure one under Operations.
        </p>
      </Group>
    </div>
  )
}

// ── Types ──────────────────────────────────────────────────────────────────

function TypesTab({ project, objName, repository, patch }: {
  project: string
  objName: string
  repository: Record<string, any>
  patch: (path: string[], value: unknown) => void
}) {
  const [live, setLive] = useState<DmsDocType[]>([])
  const seeds: any[] = repository.document_types || []
  const [selected, setSelected] = useState(0)

  const refresh = useCallback(async () => {
    try {
      const res = await listTypes(project, objName)
      setLive(res.types || [])
    } catch {
      setLive([])
    }
  }, [project, objName])

  useEffect(() => { void refresh() }, [refresh])

  const runtimeOnly = useMemo(
    () => live.filter((t) => t.source === 'runtime'),
    [live],
  )

  const type = seeds[selected]

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
      <aside style={{ width: 220, flexShrink: 0 }}>
        <h3 style={groupTitleStyle}>Designer types</h3>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {seeds.map((t: any, index: number) => (
            <li key={t.key || index}>
              <button type="button" onClick={() => setSelected(index)}
                      style={{ ...listBtnStyle,
                               background: index === selected
                                 ? 'var(--color-surface-2)' : 'transparent' }}>
                {t.title || t.key}
              </button>
            </li>
          ))}
        </ul>
        <button type="button" style={{ ...btnStyle, width: '100%', marginTop: 8 }}
                onClick={() => {
                  patch(['repository', 'document_types'], [...seeds, {
                    key: `TYPE_${seeds.length + 1}`, title: 'New kind of document',
                    search_flags: { metadata_search: true, full_text_search: true, ai_qa: true },
                    ai_mode: 'full', fields: [],
                  }])
                  setSelected(seeds.length)
                }}>
          Add a kind
        </button>

        {runtimeOnly.length > 0 ? (
          <>
            <h3 style={{ ...groupTitleStyle, marginTop: 20 }}>Added in the running app</h3>
            <p style={helpStyle}>
              Created by an administrator in the running app. Editing them here
              would have no effect — a redeploy leaves them untouched.
            </p>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {runtimeOnly.map((t) => (
                <li key={t.id} style={{ ...listBtnStyle, cursor: 'default',
                                        color: T.textSecondary, display: 'flex',
                                        alignItems: 'center', gap: 8 }}>
                  <span style={{ flex: 1, minWidth: 0 }}>{t.title}</span>
                  <FactChip>
                    {t.fields.length} detail{t.fields.length === 1 ? '' : 's'}
                  </FactChip>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </aside>

      {type ? (
        <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 16, maxWidth: 680 }}>
          <Group title="What this kind is called">
            <Field label="Title">
              <input style={inputStyle} value={type.title || ''}
                     onChange={(e) => patch(['repository', 'document_types', String(selected), 'title'],
                                            e.target.value)} />
            </Field>
            <Field label="Key" help="A fixed short code, used behind the scenes. Changing it creates a new kind rather than renaming this one.">
              <input style={inputStyle} value={type.key || ''}
                     onChange={(e) => patch(['repository', 'document_types', String(selected), 'key'],
                                            e.target.value.toUpperCase())} />
            </Field>
          </Group>

          <Group title="Retention and review">
            <Field label="Keep for (years)" help="After this, the document is withdrawn and taken out of search. It is never deleted automatically — somebody has to do that deliberately.">
              <input type="number" style={inputStyle} value={type.retention?.years ?? 0}
                     onChange={(e) => patch(['repository', 'document_types', String(selected), 'retention', 'years'],
                                            Number(e.target.value) || 0)} />
            </Field>
            <Field label="Check it again every (months)" help="Reminds whoever looks after it. The document stays in force until they act on the reminder.">
              <input type="number" style={inputStyle} value={type.review?.months ?? 0}
                     onChange={(e) => patch(['repository', 'document_types', String(selected), 'review', 'months'],
                                            Number(e.target.value) || 0)} />
            </Field>
          </Group>

          <Group title="Finding and answering">
            {FLAGS.map((flag) => (
              <Toggle
                key={flag.key}
                label={flag.label}
                help={flag.help}
                checked={type.search_flags?.[flag.key] !== false}
                onChange={(v) => patch(
                  ['repository', 'document_types', String(selected), 'search_flags', flag.key], v)}
              />
            ))}
            <Field label="Can the assistant read these?">
              <select style={inputStyle} value={type.ai_mode || 'full'}
                      onChange={(e) => patch(['repository', 'document_types', String(selected), 'ai_mode'],
                                             e.target.value)}>
                {AI_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </Field>
            <p style={helpStyle}>
              {AI_MODES.find((m) => m.value === (type.ai_mode || 'full'))?.help}
            </p>
          </Group>

          <Group title="Files this kind accepts"
                 note="Checked against what the file really is, not its name — renaming a spreadsheet to .pdf does not get it past this. Leave a box empty for no limit.">
            <div style={fieldRowStyle}>
              <Field label="Extensions" help="Comma separated, e.g. pdf, docx. Empty = any.">
                <input style={inputStyle}
                       value={(type.file_rules?.extensions || []).join(', ')}
                       placeholder="pdf, docx"
                       onChange={(e) => patch(
                         ['repository', 'document_types', String(selected), 'file_rules', 'extensions'],
                         e.target.value.split(',').map((x) => x.trim().replace(/^\./, '').toLowerCase()).filter(Boolean))} />
              </Field>
              <Field label="Smallest (KB)">
                <input style={{ ...inputStyle, width: 100 }} type="number" min={0}
                       value={type.file_rules?.min_size_kb || ''}
                       onChange={(e) => patch(
                         ['repository', 'document_types', String(selected), 'file_rules', 'min_size_kb'],
                         Number(e.target.value) || 0)} />
              </Field>
              <Field label="Largest (MB)">
                <input style={{ ...inputStyle, width: 100 }} type="number" min={0}
                       value={type.file_rules?.max_size_mb || ''}
                       onChange={(e) => patch(
                         ['repository', 'document_types', String(selected), 'file_rules', 'max_size_mb'],
                         Number(e.target.value) || 0)} />
              </Field>
            </div>
            <p style={helpStyle}>
              The file's type, name and size are shown to every reader beside
              the metadata — read from the stored bytes, not typed by anyone.
            </p>
          </Group>

          <Group title="How a document of this kind is created"
                 note="Uploaded is the default and is how every repository worked before the editor existed. Turn it on for the documents whose WORDING is the record — a policy, an SOP, a specification — so they are written, versioned, compared and approved in one place instead of being drafted in a word processor and filed as a flattened copy.">
            <Field label="Authoring">
              <select style={inputStyle}
                      value={type.authoring?.mode || 'off'}
                      onChange={(e) => patch(
                        ['repository', 'document_types', String(selected), 'authoring', 'mode'],
                        e.target.value)}>
                {AUTHORING_MODES.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </Field>
            <p style={helpStyle}>
              {AUTHORING_MODES.find((m) => m.value === (type.authoring?.mode || 'off'))?.help}
            </p>
            {String(type.authoring?.mode || 'off') !== 'off' ? (
              <Field label="Template"
                     help="The formatting a document of this type opens with — its page, fonts, heading styles and document control section. Templates are defined on the Templates tab.">
                <select style={inputStyle}
                        value={type.authoring?.template || 'GENERAL'}
                        onChange={(e) => patch(
                          ['repository', 'document_types', String(selected), 'authoring', 'template'],
                          e.target.value)}>
                  {(repository.templates || []).map((t: any) => (
                    <option key={t.key} value={t.key}>{t.name || t.key}</option>
                  ))}
                </select>
              </Field>
            ) : null}
          </Group>

          <Group title="Where the details are kept"
                 note="Native fields are defined below. Bind a TRANSACTION object instead when the metadata deserves the transaction designer's full machinery — its columns, lookups, validations, business logic and encrypted columns all apply, because the record is saved through the engine.">
            <Field label="Source">
              <select style={inputStyle}
                      value={type.metadata_source || 'native'}
                      onChange={(e) => patch(
                        ['repository', 'document_types', String(selected), 'metadata_source'],
                        e.target.value)}>
                <option value="native">Fields defined here</option>
                <option value="transaction">A transaction object</option>
              </select>
            </Field>
            {String(type.metadata_source || 'native') === 'transaction' ? (
              <>
                <div style={fieldRowStyle}>
                  <Field label="Transaction object" help="The object whose record holds this document's details.">
                    <input style={inputStyle} value={type.obj_name || ''}
                           placeholder="CONTRACT_META"
                           onChange={(e) => patch(
                             ['repository', 'document_types', String(selected), 'obj_name'],
                             e.target.value.toUpperCase())} />
                  </Field>
                  <Field label="Preview columns" help="Copied here as well, so the library can search and list them quickly.">
                    <input style={inputStyle}
                           value={(type.preview_fields || []).join(', ')}
                           placeholder="COUNTERPARTY, EFFECTIVE_DATE"
                           onChange={(e) => patch(
                             ['repository', 'document_types', String(selected), 'preview_fields'],
                             e.target.value.split(',').map((x) => x.trim().toUpperCase()).filter(Boolean))} />
                  </Field>
                </div>
                <p style={helpStyle}>
                  The record is the truth; the preview columns are a copy kept
                  for search. A document type bound to an object that is not
                  deployed refuses uploads rather than filing them untyped.
                </p>
              </>
            ) : null}
          </Group>

          <FieldsEditor
            project={project}
            fields={type.fields || []}
            onChange={(next) => patch(
              ['repository', 'document_types', String(selected), 'fields'], next)}
          />
        </div>
      ) : (
        <p style={mutedBoxStyle}>Add a document type to begin.</p>
      )}
    </div>
  )
}

/**
 * A metadata field's default is written in the SAME language a transaction
 * column's `default_value` is — same picker, same validator, same tokens —
 * because a designer should not have to learn a second vocabulary for the
 * same idea. What differs is only what a REPOSITORY can honour: there is no
 * parent row to number a line within, and no per-device series, so those two
 * auto-generate functions are not offered.
 */
const DMS_AUTO_GEN = ['auto_generate()', 'auto_generate_prefixed()'] as const

/**
 * The repository's own `{{...}}` tokens — the ones that need a DOCUMENT to
 * mean anything, plus the user-information set. Offered in the same picker as
 * everything else so there is one menu, not two.
 */
const DMS_TOKEN_GROUPS = [
  {
    title: 'Who is filing',
    entries: [
      { token: '{{user_name}}', label: 'The name of the person filing the document' },
      { token: '{{user_email}}', label: 'Their email address' },
      { token: '{{user_id}}', label: 'Their user key — what a USER-typed field stores' },
      { token: '{{tenant}}', label: 'The organisation they are filing for' },
      { token: '{{ip}}', label: 'The address they filed from' },
    ],
  },
  {
    title: 'This document',
    entries: [
      { token: '{{doctype}}', label: 'The kind of document — this type’s key' },
      { token: '{{title}}', label: 'The document’s name' },
      { token: '{{filename}}', label: 'The name of the file that was uploaded' },
      { token: '{{today}}', label: 'The date it is filed' },
      { token: '{{today+12m}}', label: 'A period after filing (also +30d, +2y) — a review date' },
      { token: '{{year}}', label: 'The year it is filed' },
    ],
  },
] as const

const DEFAULT_HELP =
  'What this field is filled with. A constant ("Internal"), or a rule in the '
  + 'same language a transaction column uses: auto_generate() for a reference '
  + 'number, today() / :calendar_month_end for dates, :audit_add_user or '
  + '{{user_name}} for who filed it, or a project variable. Applied on upload; '
  + 'unless the field is system-filled, a value the uploader types wins.'

function FieldsEditor({ project, fields, onChange }: {
  project: string
  fields: any[]
  onChange: (next: any[]) => void
}) {
  const [projectVars, setProjectVars] = useState<ProjectVariable[]>([])
  const [editingSeries, setEditingSeries] = useState<number | null>(null)
  // Which field each borrowed modal is open on. Four separate slots rather
  // than one union: they are independent decisions and a single "editing"
  // state would make opening one close another.
  const [editingLookup, setEditingLookup] = useState<number | null>(null)
  const [editingDisplay, setEditingDisplay] = useState<number | null>(null)
  const [editingRules, setEditingRules] = useState<number | null>(null)
  const [editingChange, setEditingChange] = useState<number | null>(null)
  // Which of the field's validation rules the editor is on — `-1` is "a new
  // one", which is why it is not simply an index into the list.
  const [editingRuleIndex, setEditingRuleIndex] = useState(-1)

  // For the `:<name>` group in the picker and for validating what is typed. A
  // project with none simply gets an empty group saying where to define them.
  useEffect(() => {
    let live = true
    if (!project) return
    listProjectVariables(project)
      .then((rows) => { if (live) setProjectVars(rows || []) })
      .catch(() => { /* the picker degrades to its other groups */ })
    return () => { live = false }
  }, [project])

  const update = (index: number, key: string, value: unknown) => {
    const next = fields.map((f, i) => (i === index ? { ...f, [key]: value } : f))
    onChange(next)
  }

  /** Applied together, because two `update` calls in a row would both read the
   *  same `fields` snapshot and the second would undo the first. */
  const updateMany = (index: number, patch: Record<string, unknown>) => {
    onChange(fields.map((f, i) => (i === index ? { ...f, ...patch } : f)))
  }

  const defaultOf = (field: any): string =>
    typeof field.default === 'string' ? field.default
      : field.default == null ? '' : String(field.default)

  const seriesField = editingSeries == null ? null : fields[editingSeries]

  /**
   * A metadata field, shaped as the transaction COLUMN the borrowed editors
   * read and write.
   *
   * The same adapter the engine makes on the server (`field_defaults.as_column`)
   * — and for the same reason: the lookup editor, the display-format editor,
   * the validation-rule editor and the item_change editors are the transaction
   * designer's, unchanged, so a repository field is handed to them as the thing
   * they already understand. Authoring these through a second, similar-looking
   * set of editors is precisely how the two would drift.
   */
  const asColumn = (field: any) => ({
    name: String(field?.key || ''),
    db_name: String(field?.key || ''),
    heading: String(field?.title || field?.key || ''),
    col_type: DMS_TO_COL_TYPE[String(field?.data_type || 'VARCHAR').toUpperCase()]
      || 'VARCHAR',
    lookup: typeof field?.lookup === 'string' ? field.lookup : '',
    lookup_filter: field?.lookup_filter || '',
    lookup_filter_rules: field?.lookup_filter_rules || undefined,
    lookup_sql: field?.lookup_sql || '',
    lookup_title: field?.lookup_title || '',
    lookup_sql_data_col: field?.lookup_sql_data_col || '',
    lookup_add_object: field?.lookup_add_object || '',
    edit_mask: field?.edit_mask || { mask: '', values: [] },
    display: field?.display || undefined,
    validations: field?.validations || [],
    item_change: field?.item_change || null,
  }) as any

  /** The sibling field names a rule, a filter or a fill may reference — the
   *  `{form.X}` namespace, which for a repository is simply its own type. */
  const siblingNames = (self: number | null) => fields
    .map((f) => String(f.key || ''))
    .filter((k, i) => k && i !== self)

  const lookupField = editingLookup == null ? null : fields[editingLookup]
  const displayField = editingDisplay == null ? null : fields[editingDisplay]
  const rulesField = editingRules == null ? null : fields[editingRules]
  const changeField = editingChange == null ? null : fields[editingChange]

  return (
    <Group title="Details this kind asks for"
           note="Values are stored in typed columns, so metadata search is a real indexed query rather than a scan. Marking a field sensitive gives it the same treatment as a sensitive transaction column — encrypted at rest, masked for anyone outside its reveal roles, and never sent to a model. A DEFAULT is what the type fills in, written in the same language a transaction column's default is: a constant, an auto-generated reference number, a date rule, who is filing, or a project variable. It counts as a real value, because the type's designer declared it; an AI-extracted value does not, because a model guessed it. Mark a field SYSTEM-FILLED and it is no longer a suggestion — the repository resolves it on every write and nobody is offered a box for it.">
      {fields.length === 0
        ? <p style={helpStyle}>Nothing yet — this kind asks for no extra details.</p>
        : null}
      {fields.map((field, index) => {
        const raw = defaultOf(field)
        const tokenError = validateTokenDefault(raw, projectVars)
        const prefixed = isPrefixedSeriesToken(raw)
        const managed = Boolean(field.protect)
        return (
          <div key={index} style={fieldCardStyle}>
            <div style={fieldRowStyle}>
              <input style={{ ...inputStyle, width: 150 }} value={field.key || ''}
                     placeholder="KEY"
                     onChange={(e) => update(index, 'key', e.target.value.toUpperCase())} />
              <input style={{ ...inputStyle, flex: 1, minWidth: 120 }} value={field.title || ''}
                     placeholder="Label"
                     onChange={(e) => update(index, 'title', e.target.value)} />
              <select style={{ ...inputStyle, width: 110 }} value={field.data_type || 'VARCHAR'}
                      onChange={(e) => update(index, 'data_type', e.target.value)}>
                {['VARCHAR', 'NUMERIC', 'DATE', 'BOOLEAN', 'USER', 'JSON', 'IMAGE'].map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <label style={checkLabelStyle}>
                <input type="checkbox" checked={Boolean(field.mandatory)}
                       onChange={(e) => update(index, 'mandatory', e.target.checked)} />
                required
              </label>
              <label style={checkLabelStyle} title="Encrypted at rest, masked on read, never embedded">
                <input type="checkbox" checked={Boolean(field.secure)}
                       onChange={(e) => updateMany(index, {
                         secure: e.target.checked
                           ? { mode: 'encrypt', search: 'exact',
                               mask: 'partial', reveal_roles: [] }
                           : null,
                         // A sensitive field is never defaulted, so it cannot
                         // be system-filled either — clear the switch rather
                         // than leave one that now does nothing.
                         ...(e.target.checked ? { protect: false } : {}),
                       })} />
                sensitive
              </label>
              <button type="button" style={btnStyle}
                      onClick={() => onChange(fields.filter((_, i) => i !== index))}>
                Remove
              </button>
            </div>

            {/* A sensitive field is never defaulted: the value would be
                encrypted and then read back as one somebody entered. */}
            {!field.secure ? (
              <div style={fieldRowStyle}>
                <span style={{ ...checkLabelStyle, width: 60 }}>Default</span>
                <input
                  style={{
                    ...inputStyle, flex: 1, minWidth: 180,
                    border: tokenError ? `1px solid ${T.error}` : inputStyle.border,
                  }}
                  value={raw}
                  placeholder="auto_generate(), today(), {{user_name}}, :calendar_month_end, or a constant"
                  title={DEFAULT_HELP}
                  onChange={(e) => update(index, 'default', e.target.value || null)}
                />
                <DefaultValuePicker
                  projectVars={projectVars}
                  current={raw}
                  autoGenTokens={DMS_AUTO_GEN}
                  extraGroups={DMS_TOKEN_GROUPS}
                  onPick={(token) => {
                    updateMany(index, {
                      default: token || null,
                      // Clearing the default clears the switch with it: a
                      // system-filled field with nothing to fill it is a field
                      // nobody can ever populate.
                      ...(token ? {} : { protect: false }),
                    })
                    // The prefixed token does nothing on its own — it needs
                    // parts. Open the builder rather than leaving the designer
                    // to discover a second step.
                    if (isPrefixedSeriesToken(token)) setEditingSeries(index)
                  }}
                />
                <label style={checkLabelStyle}
                       title="The repository fills this on every write and the person filing is never offered a box for it. A value posted for it is discarded, whichever screen it came from.">
                  <input type="checkbox" checked={managed} disabled={!raw}
                         onChange={(e) => update(index, 'protect', e.target.checked)} />
                  system-filled
                </label>
              </div>
            ) : null}

            {tokenError ? (
              <div style={{ ...helpStyle, color: T.error }}>{tokenError}</div>
            ) : managed ? (
              <div style={helpStyle}>
                Always this value. The upload panel shows it read-only, and the
                server discards anything posted for it.
              </div>
            ) : null}

            {!field.secure && prefixed ? (
              <div style={fieldRowStyle}>
                <span style={{ ...checkLabelStyle, width: 60 }}>Id rules</span>
                <button type="button" style={btnStyle}
                        onClick={() => setEditingSeries(index)}>
                  {seriesSummary(field as any)
                    || 'Not configured yet — no number can be generated until it is'}
                </button>
              </div>
            ) : null}

            {field.secure ? (
              <SecureFieldOptions
                spec={field.secure}
                onChange={(next) => update(index, 'secure', next)}
              />
            ) : null}

            {/* The widget. Shared with the transaction designer, so a mask
                added there is offered here on the same day. */}
            <EditMaskEditor
              value={field.edit_mask}
              onChange={(mask) => update(index, 'edit_mask',
                mask.mask ? mask : null)}
              labelStyle={{ ...checkLabelStyle, display: 'block', marginTop: 6 }}
              inputStyle={inputStyle}
              addBtnStyle={btnStyle}
              label="Captured as"
            />

            {/* The four capabilities a transaction column has, each opening the
                transaction designer's own editor. A repository field is handed
                to them as the column they already understand (`asColumn`), and
                what comes back is written straight onto the field — the shapes
                are identical because the engine runs them with the same code. */}
            <div style={{ ...fieldRowStyle, flexWrap: 'wrap' }}>
              <button type="button" style={btnStyle}
                      title="Read this field's values from a master table instead of typing a list. The reader gets a searchable picker; the values cannot drift from the table."
                      onClick={() => setEditingLookup(index)}>
                {typeof field.lookup === 'string' && field.lookup
                  ? `Looks up ${field.lookup}`
                  : field.lookup_sql ? 'Looks up (SQL)' : 'Look up from a table…'}
              </button>
              <button type="button" style={btnStyle}
                      title="How the value is SHOWN in the library and on the document — a tag, a bar, a link, a flag. Display only: it never changes what is captured or stored."
                      onClick={() => setEditingDisplay(index)}>
                {field.display?.type && field.display.type !== 'text'
                  ? `Shown as ${field.display.type}` : 'Show as…'}
              </button>
              <button type="button" style={btnStyle}
                      title="Rules checked when the metadata is saved. Errors refuse the save; warnings can be acknowledged."
                      onClick={() => { setEditingRuleIndex(-1); setEditingRules(index) }}>
                {(field.validations || []).length
                  ? `${field.validations.length} check${field.validations.length === 1 ? '' : 's'}`
                  : 'Add a check…'}
              </button>
              <button type="button" style={btnStyle}
                      title="What happens when this field changes — fill in other fields, or lock them."
                      onClick={() => setEditingChange(index)}>
                {field.item_change ? 'Changes other fields' : 'On change…'}
              </button>
            </div>

            {/* Two settings that quietly cancel each other out. Neither is an
                error the save can refuse — both are legal on their own — so
                the only place they can be caught is here, before somebody
                configures a lookup and then reports that it does nothing. */}
            {conflictsOf(field).map((why) => (
              <p key={why} style={conflictStyle}>{why}</p>
            ))}

            {/* The rules themselves, so a check can be read, edited or removed
                without opening the editor to find out what it is. */}
            {(field.validations || []).length ? (
              <div style={{ display: 'grid', gap: 3, width: '100%',
                            padding: '2px 0 2px 8px',
                            borderLeft: `2px solid ${T.border}` }}>
                {(field.validations || []).map((rule: any, ri: number) => (
                  <div key={ri} style={fieldRowStyle}>
                    <button type="button"
                            style={{ ...btnStyle, flex: 1, textAlign: 'left' }}
                            onClick={() => {
                              setEditingRuleIndex(ri)
                              setEditingRules(index)
                            }}>
                      {ruleSummary(rule)}
                    </button>
                    <button type="button" style={btnStyle}
                            title="Remove this check"
                            onClick={() => update(index, 'validations',
                              (field.validations || []).filter((_: any, i: number) => i !== ri))}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        )
      })}
      <button type="button" style={btnStyle}
              onClick={() => onChange([...fields, {
                key: `FIELD_${fields.length + 1}`, title: 'New field',
                data_type: 'VARCHAR', mandatory: false,
              }])}>
        Add field
      </button>

      {/* The SAME builder the transaction designer uses for a prefixed id —
          the prefix parts are resolved by the same engine code, so authoring
          them through a second, similar-looking editor would be how the two
          drift apart. */}
      <SeriesEditor
        open={editingSeries != null}
        onClose={() => setEditingSeries(null)}
        column={seriesField ? ({ series: seriesField.series } as any) : null}
        formColumns={siblingNames(editingSeries)}
        project={project}
        onSave={(series) => {
          if (editingSeries != null) update(editingSeries, 'series', series)
          setEditingSeries(null)
        }}
      />

      {/* Where the values come from. The editor writes a `TABLE(DISPLAY, DATA)`
          spec plus its filter — read at runtime by the SAME engine call the
          visual criteria picker makes, which is why the shapes match exactly.
          A field that had a hand-typed list keeps it until a table is chosen;
          the two are alternative answers to the same question and the model
          normaliser drops whichever is not in play. */}
      <LookupEditor
        open={editingLookup != null}
        onClose={() => setEditingLookup(null)}
        column={lookupField ? asColumn(lookupField) : null}
        formColumns={siblingNames(editingLookup)}
        headerColumns={[]}
        project={project}
        onSave={(patch) => {
          if (editingLookup != null) updateMany(editingLookup, { ...patch })
          setEditingLookup(null)
        }}
      />

      {/* How it LOOKS. Same catalogue, same live preview, same `<DisplayCell>`
          doing the rendering here and at runtime. */}
      <DisplayFormatEditor
        open={editingDisplay != null}
        onClose={() => setEditingDisplay(null)}
        column={displayField ? asColumn(displayField) : null}
        formColumns={siblingNames(editingDisplay)}
        onSave={(display) => {
          if (editingDisplay != null) update(editingDisplay, 'display', display || null)
          setEditingDisplay(null)
        }}
      />

      {/* One validation rule. `editingRuleIndex` of -1 is a NEW rule, which is
          why the list is spliced rather than indexed blindly. */}
      <ValidationRuleEditor
        open={editingRules != null}
        onClose={() => setEditingRules(null)}
        value={(rulesField?.validations || [])[editingRuleIndex] ?? null}
        formColumnNames={siblingNames(null)}
        headerColumnNames={[]}
        project={project}
        title="When is this value acceptable?"
        onSave={(rule) => {
          if (editingRules != null) {
            const existing = [...(fields[editingRules]?.validations || [])]
            if (editingRuleIndex >= 0) existing[editingRuleIndex] = rule
            else existing.push(rule)
            update(editingRules, 'validations', existing)
          }
          setEditingRules(null)
        }}
      />

      {/* What a change to this field DOES. Rule mode fills (and locks) other
          fields from a master table or an expression; the other two modes hand
          the decision to business logic or an API. Run at runtime by the
          engine's own item_change dispatcher. */}
      <ItemChangeRuleEditor
        open={editingChange != null && icMode(changeField) === 'rule'}
        onClose={() => setEditingChange(null)}
        value={(changeField?.item_change || null) as any}
        triggerColumnName={String(changeField?.key || '')}
        formColumnNames={siblingNames(null)}
        headerColumnNames={[]}
        project={project}
        onSave={(rule) => {
          if (editingChange != null) update(editingChange, 'item_change', rule)
          setEditingChange(null)
        }}
      />
      <ItemChangeCodeEditor
        open={editingChange != null && icMode(changeField) !== 'rule'}
        onClose={() => setEditingChange(null)}
        value={(changeField?.item_change || null) as any}
        formColumnNames={siblingNames(null)}
        headerColumnNames={[]}
        project={project}
        onSave={(ic) => {
          if (editingChange != null) update(editingChange, 'item_change', ic)
          setEditingChange(null)
        }}
      />
    </Group>
  )
}

/**
 * What this field asks for that it will not get.
 *
 * A metadata field can be given a capture WIDGET, a table LOOKUP, checks and
 * an on-change rule independently, and two of those combinations are dead on
 * arrival at runtime:
 *
 * - a declared widget draws its own control, so the lookup's picker never
 *   appears beside it (`MetadataFieldInput` honours the widget first, which is
 *   right — a designer who asked for radio buttons should get radio buttons);
 * - a SYSTEM-FILLED field is never offered a box, and the server discards a
 *   posted value for it before any rule runs, so both a lookup and a check on
 *   one are configuration nobody will ever reach.
 */
function conflictsOf(field: any): string[] {
  const out: string[] = []
  const hasLookup = Boolean(
    (typeof field?.lookup === 'string' && field.lookup.trim())
    || String(field?.lookup_sql || '').trim())
  const widget = String(field?.edit_mask?.mask || '').trim()
  const checks = (field?.validations || []).length
  if (hasLookup && widget) {
    out.push(`This field is captured as "${widget}", and that widget draws its `
      + 'own control — the look-up picker will not appear beside it. Clear one '
      + 'of the two.')
  }
  if (field?.protect && (hasLookup || checks)) {
    const what = hasLookup && checks ? 'The look-up and the checks are'
      : hasLookup ? 'The look-up is' : 'The checks are'
    out.push(`${what} never reached: a system-filled field is not typed by the `
      + 'person filing, and the repository discards anything posted for it. '
      + 'Untick "system-filled" if this is a value somebody enters.')
  }
  return out
}

/**
 * Which item_change editor to open.
 *
 * A field with no rule yet gets the RULE editor: filling one field from another
 * is what almost every on-change rule is, and business logic is the deliberate
 * step past it. A field that already carries a business-logic or API rule opens
 * in the editor that authored it.
 */
function icMode(field: any): string {
  const mode = String(field?.item_change?.mode || '').trim().toLowerCase()
  return mode === 'business_logic' || mode === 'rest_api' || mode === 'code'
    ? mode : 'rule'
}

/** One line describing a validation rule, so the list reads without opening
 *  each one. Falls back to the rule's own name, then its kind. */
function ruleSummary(rule: any): string {
  if (typeof rule === 'string') return rule
  const name = String(rule?.name || '').trim()
  const kind = String(rule?.type || '').trim().toLowerCase()
  const severity = String(rule?.severity || 'E').toUpperCase()
  const tag = severity === 'W' ? ' (warning)' : severity === 'P' ? ' (note)' : ''
  if (name) return `${name}${tag}`
  if (kind === 'expression') return `Expression: ${rule?.expression || '—'}${tag}`
  if (kind === 'business_logic') {
    return `Business logic: ${rule?.business_logic?.method || '—'}${tag}`
  }
  if (kind === 'rest_api') return `API call${tag}`
  if (kind === 'builtin') {
    return `${rule?.builtin?.kind || 'Built-in check'}${tag}`
  }
  return `Check${tag}`
}

/** DMS field data type → the transaction `col_type` the borrowed editors read.
 *  The mirror of `field_defaults._COL_TYPE` on the server. */
const DMS_TO_COL_TYPE: Record<string, string> = {
  VARCHAR: 'VARCHAR', TEXT: 'VARCHAR', USER: 'VARCHAR', JSON: 'VARCHAR',
  IMAGE: 'VARCHAR', FILE: 'VARCHAR',
  NUMERIC: 'NUMBER', DATE: 'DATE', BOOLEAN: 'BOOLEAN',
}

/**
 * How a sensitive metadata field behaves — the SAME vocabulary a sensitive
 * transaction column uses (`field_crypto.SecureSpec`), so the two cannot drift.
 *
 * The default that catches people out is stated here rather than left to be
 * discovered: **empty reveal roles means NOBODY**. It is the safe default and
 * the opposite of what "who can see it? nobody listed, so everyone" would
 * suggest, so a field marked sensitive and left alone is masked for every
 * reader including the person who authored it.
 */
function SecureFieldOptions({ spec, onChange }: {
  spec: Record<string, any>
  onChange: (next: Record<string, any>) => void
}) {
  const roles: string[] = Array.isArray(spec.reveal_roles) ? spec.reveal_roles : []
  const patch = (key: string, value: unknown) => onChange({ ...spec, [key]: value })
  return (
    <div style={{ display: 'grid', gap: 6, width: '100%',
                  padding: '6px 0 2px 8px',
                  borderLeft: '2px solid var(--color-border)' }}>
      <div style={fieldRowStyle}>
        <span style={{ ...helpStyle, width: 90 }}>Shown to</span>
        <input
          style={{ ...inputStyle, flex: 1, minWidth: 160 }}
          value={roles.join(', ')}
          placeholder="roles that may see the real value"
          onChange={(e) => patch('reveal_roles',
            e.target.value.split(',').map((r) => r.trim().toLowerCase())
              .filter(Boolean))}
        />
      </div>
      {roles.length === 0 ? (
        <p style={{ ...helpStyle, color: 'var(--color-warning)' }}>
          No roles listed, so NOBODY sees this value — every reader gets the
          mask, and the assistant only ever sees a placeholder. Name at least
          one role if somebody is meant to read it.
        </p>
      ) : null}
      <div style={fieldRowStyle}>
        <span style={{ ...helpStyle, width: 90 }}>Others see</span>
        <select style={{ ...inputStyle, width: 130 }}
                value={String(spec.mask || 'partial')}
                onChange={(e) => patch('mask', e.target.value)}>
          <option value="partial">Partly masked</option>
          <option value="replace">A fixed replacement</option>
          <option value="null">Nothing at all</option>
          <option value="hash">A hash</option>
          <option value="disallow">An error</option>
        </select>
        {String(spec.mask || '') === 'replace' ? (
          <input style={{ ...inputStyle, width: 120 }}
                 value={String(spec.replacement || '')}
                 placeholder="replacement"
                 onChange={(e) => patch('replacement', e.target.value)} />
        ) : null}
        <span style={{ ...helpStyle, width: 60 }}>Search</span>
        <select style={{ ...inputStyle, width: 140 }}
                value={String(spec.search || 'exact')}
                onChange={(e) => patch('search', e.target.value)}>
          <option value="exact">Exact match (blind index)</option>
          <option value="none">Not searchable</option>
        </select>
      </div>
    </div>
  )
}

// ── Structure ──────────────────────────────────────────────────────────────

/**
 * How the repository is organised, and what "organised" is keyed on.
 *
 * Two things live here because they are the same decision from two sides. The
 * auto-file rule decides where a document LANDS from its metadata; the business
 * area field decides who may SEE it from (usually) the same metadata. An SOP
 * library filed by business area and granted by business area is one idea
 * expressed twice, so both are set on one page.
 */
function StructureTab({ repository, patch }: {
  repository: Record<string, any>
  patch: (path: string[], value: unknown) => void
}) {
  const structure = repository.structure || {}
  const folders: any[] = structure.folders || []

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 860 }}>
      <Group title="Filing"
             note="An auto-file rule builds the folder path from a document's own metadata, so nobody files anything by hand and two people cannot disagree about where a document belongs.">
        <Field label="Mode">
          <select style={inputStyle} value={structure.mode || 'flat'}
                  onChange={(e) => patch(['repository', 'structure', 'mode'], e.target.value)}>
            <option value="flat">Flat — no folders</option>
            <option value="cabinet">Cabinets — a designed folder tree</option>
            <option value="metadata_driven">Metadata-driven — folders appear as documents are filed</option>
          </select>
        </Field>
        <Field label="Auto-file rule"
               help="A path template. Tokens: {{metadata.KEY}}, {{doctype}}, {{year}}. A token with no value stops the path there rather than creating an empty folder.">
          <input style={inputStyle} value={structure.auto_file_rule || ''}
                 placeholder="{{doctype}}/{{metadata.BUSINESS_AREA}}/{{metadata.YEAR}}"
                 onChange={(e) => patch(['repository', 'structure', 'auto_file_rule'], e.target.value)} />
        </Field>
        <Toggle label="Let users create their own folders"
                checked={structure.allow_user_folders !== false}
                onChange={(v) => patch(['repository', 'structure', 'allow_user_folders'], v)} />
      </Group>

      <Group title="Folder tree"
             note="Seeded into the repository on deploy, and never destroyed by one: a folder you remove here is LEFT in place, because documents are filed in it. Naming the document types a folder accepts is what makes a cabinet mean something — anything else is refused at upload.">
        {folders.length === 0 ? (
          <p style={helpStyle}>No folders designed. Documents land wherever the
          auto-file rule puts them, or at the root.</p>
        ) : null}
        <FolderNodes
          nodes={folders}
          onChange={(next) => patch(['repository', 'structure', 'folders'], next)}
        />
      </Group>

      <Group title="Business area"
             note="Names the metadata field that carries a document's business area. Once set, it is stamped onto every document and you can grant rights per area on the Rights tab — one grant covering every document of that area, including ones not filed yet.">
        <Field label="Metadata field"
               help="The KEY of a metadata field on your document types, e.g. BUSINESS_AREA. Leave empty if this repository is not area-scoped.">
          <input style={inputStyle} value={repository.business_area_field || ''}
                 placeholder="BUSINESS_AREA"
                 onChange={(e) => patch(['repository', 'business_area_field'],
                                        e.target.value.toUpperCase())} />
        </Field>
        <Field label="A reader's own area"
               help="Optional. A project variable resolved per logged-in user, e.g. :user_business_area. Without it, a reader's areas come only from the grants naming their roles.">
          <input style={inputStyle} value={repository.business_area_source || ''}
                 placeholder=":user_business_area"
                 onChange={(e) => patch(['repository', 'business_area_source'], e.target.value)} />
        </Field>
        <p style={helpStyle}>
          A field marked <em>sensitive</em> cannot also be the business area:
          the stamp is not encrypted, so it would publish in the clear exactly
          what the field was marked to protect.
        </p>
      </Group>
    </div>
  )
}

function FolderNodes({ nodes, onChange, depth = 0 }: {
  nodes: any[]
  onChange: (next: any[]) => void
  depth?: number
}) {
  const update = (index: number, key: string, value: unknown) =>
    onChange(nodes.map((n, i) => (i === index ? { ...n, [key]: value } : n)))

  return (
    <div style={{ display: 'grid', gap: 6,
                  paddingLeft: depth ? 16 : 0,
                  borderLeft: depth ? '2px solid var(--color-border)' : undefined }}>
      {nodes.map((node, index) => (
        <div key={index} style={{ display: 'grid', gap: 4 }}>
          <div style={fieldRowStyle}>
            <input style={{ ...inputStyle, width: 180 }} value={node.name || ''}
                   placeholder="Folder name"
                   onChange={(e) => update(index, 'name', e.target.value)} />
            <input style={{ ...inputStyle, flex: 1, minWidth: 160 }}
                   value={(node.allowed_doctypes || []).join(', ')}
                   placeholder="document types it accepts (empty = any)"
                   onChange={(e) => update(index, 'allowed_doctypes',
                     e.target.value.split(',').map((x) => x.trim().toUpperCase()).filter(Boolean))} />
            {depth < 6 ? (
              <button type="button" style={btnStyle}
                      onClick={() => update(index, 'children',
                                            [...(node.children || []), { name: 'New folder' }])}>
                Add child
              </button>
            ) : null}
            <button type="button" style={btnStyle}
                    onClick={() => onChange(nodes.filter((_, i) => i !== index))}>
              Remove
            </button>
          </div>
          {(node.children || []).length ? (
            <FolderNodes
              nodes={node.children}
              depth={depth + 1}
              onChange={(next) => update(index, 'children', next)}
            />
          ) : null}
        </div>
      ))}
      <div>
        <button type="button" style={btnStyle}
                onClick={() => onChange([...nodes, { name: 'New folder' }])}>
          Add folder
        </button>
      </div>
    </div>
  )
}

// ── Rights ─────────────────────────────────────────────────────────────────

function RightsTab({ project, objName, repository, patch }: {
  project: string
  objName: string
  repository: Record<string, any>
  patch: (path: string[], value: unknown) => void
}) {
  const [acl, setAcl] = useState<Record<string, any>[]>([])
  const [busy, setBusy] = useState(false)
  const matrix: any[] = repository.rights_matrix || []

  const refresh = useCallback(async () => {
    try {
      const res = await listAcl(project, objName)
      setAcl(res.acl || [])
    } catch {
      setAcl([])
    }
  }, [project, objName])

  useEffect(() => { void refresh() }, [refresh])

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 860 }}>
      <Group title="The starting point (set in the design)"
             note="Everything starts closed. This is used only while NO grant has been made anywhere in the library; the moment one is, nothing is permitted unless a grant says so. That is the opposite of the platform's usual default, and it is deliberate for documents.">
        {/* Said out loud, not just in the note above. Once a grant exists,
            every tick on this screen is a record of intent and nothing more —
            and somebody ticking and unticking boxes here, watching the running
            application not change, has no way to tell that from a bug. */}
        {acl.length > 0 ? (
          <p style={setupWarnStyle}>
            <strong>These ticks are not what the application is using.</strong>{' '}
            {acl.length} grant{acl.length === 1 ? ' has' : 's have'} been made
            in the library below, and the moment the first one was, the grants
            became the whole policy — this matrix is only the starting point
            for a repository that has none. Change a grant to change what
            people can do; edit these ticks to record what the design intends.
          </p>
        ) : null}
        {matrix.length === 0 ? (
          <p style={helpStyle}>
            Nothing set. With no grants either, only administrators and the
            person who filed a document can see it.
          </p>
        ) : null}
        {matrix.map((entry: any, index: number) => (
          <div key={index} style={{ display: 'grid', gap: 8 }}>
          <div style={fieldRowStyle}>
            <input style={{ ...inputStyle, width: 160 }} value={entry.role || ''}
                   placeholder="role"
                   onChange={(e) => patch(['repository', 'rights_matrix', String(index), 'role'],
                                          e.target.value)} />
            {RIGHTS.map((right) => (
              <label key={right} style={checkLabelStyle} title={RIGHT_HELP[right]}>
                <input type="checkbox" checked={Boolean(entry[right])}
                       onChange={(e) => patch(
                         ['repository', 'rights_matrix', String(index), right], e.target.checked)} />
                {RIGHT_LABELS[right] || right}
              </label>
            ))}
            <button type="button" style={btnStyle}
                    onClick={() => patch(['repository', 'rights_matrix'],
                                         matrix.filter((_, i) => i !== index))}>
              Remove
            </button>
          </div>
          {/* The ticks, said as sentences. See `plainRights`. */}
          <div style={plainWordsStyle}>
            <strong style={{ fontWeight: 600 }}>
              {entry.role ? `Anyone in "${entry.role}"` : 'This role'} —
            </strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {plainRights(entry).map((line) => (
                <li key={line} style={{ marginBottom: 2 }}>{line}</li>
              ))}
            </ul>
          </div>
          </div>
        ))}
        <button type="button" style={btnStyle}
                onClick={() => patch(['repository', 'rights_matrix'],
                                     [...matrix, { role: '', view: true }])}>
          Add a group
        </button>
      </Group>

      <Group title="Grants made since (live, in the database)"
             note="Most specific scope wins: document beats type beats folder beats repository. Prefer a grant at repository or folder scope; per-document rows are the exception, not the pattern — they are what makes a large repository unmanageable.">
        <GrantForm project={project} objName={objName} busy={busy}
                   setBusy={setBusy} onSaved={refresh} />
        {acl.length === 0 ? (
          <p style={helpStyle}>Nothing granted yet — the starting point above is what applies.</p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <Th>Scope</Th><Th>Principal</Th>
                {RIGHTS.map((r) => <Th key={r}>{RIGHT_LABELS[r] || r}</Th>)}
              </tr>
            </thead>
            <tbody>
              {acl.map((row) => (
                <tr key={String(row.id)} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={tdStyle}>{String(row.scope)}{row.scope_id ? ` · ${String(row.scope_id).slice(0, 8)}` : ''}</td>
                  <td style={tdStyle}>{String(row.principal_kind)}:{String(row.principal_id)}</td>
                  {RIGHTS.map((r) => (
                    <td key={r} style={tdStyle}>{Number(row[`can_${r}`]) ? '✓' : ''}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Group>
    </div>
  )
}

/**
 * One grant: who, at what scope, with which rights.
 *
 * All-false rights DELETE the row rather than storing a row of noes — and that
 * matters, because "a row exists" is what switches a repository out of the
 * model's baseline matrix into deny-by-absence. Clearing every box is how you
 * take a grant back.
 */
function GrantForm({ project, objName, busy, setBusy, onSaved }: {
  project: string
  objName: string
  busy: boolean
  setBusy: (v: boolean) => void
  onSaved: () => Promise<void> | void
}) {
  const [scope, setScope] = useState<string>('repo')
  const [scopeId, setScopeId] = useState('')
  const [principalKind, setPrincipalKind] = useState('role')
  const [principalId, setPrincipalId] = useState('')
  const [rights, setRights] = useState<Record<string, boolean>>({ view: true })
  const spec = SCOPES.find((s) => s.value === scope) || SCOPES[0]

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={fieldRowStyle}>
        <select style={{ ...inputStyle, width: 170 }} value={scope}
                onChange={(e) => { setScope(e.target.value); setScopeId('') }}>
          {SCOPES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        {spec.needsId ? (
          <input style={{ ...inputStyle, width: 190 }} value={scopeId}
                 placeholder={scope === 'business_area'
                   ? 'business area value' : `${scope} id`}
                 onChange={(e) => setScopeId(e.target.value)} />
        ) : null}
        <select style={{ ...inputStyle, width: 90 }} value={principalKind}
                onChange={(e) => setPrincipalKind(e.target.value)}>
          <option value="role">Role</option>
          <option value="user">User</option>
        </select>
        <input style={{ ...inputStyle, width: 170 }} value={principalId}
               placeholder={principalKind === 'role' ? 'role name' : 'user id'}
               onChange={(e) => setPrincipalId(e.target.value)} />
      </div>
      <p style={helpStyle}>{spec.help}</p>
      <div style={fieldRowStyle}>
        {RIGHTS.map((right) => (
          <label key={right} style={checkLabelStyle} title={RIGHT_HELP[right]}>
            <input type="checkbox" checked={Boolean(rights[right])}
                   onChange={(e) => setRights((prior) => ({
                     ...prior, [right]: e.target.checked }))} />
            {right}
          </label>
        ))}
      </div>
      <div style={fieldRowStyle}>
        <button type="button" style={btnStyle}
                disabled={busy || !principalId || (spec.needsId && !scopeId)}
                onClick={async () => {
                  setBusy(true)
                  try {
                    await saveAcl(project, objName, {
                      scope, scope_id: scopeId,
                      principal_kind: principalKind,
                      principal_id: principalId,
                      rights,
                    })
                    setPrincipalId('')
                    setScopeId('')
                    setRights({ view: true })
                    await onSaved()
                    toast.success(Object.values(rights).some(Boolean)
                      ? 'Grant saved' : 'Grant removed')
                  } catch (grantError) {
                    toast.error(grantError instanceof Error
                      ? grantError.message : 'Could not save the grant')
                  } finally {
                    setBusy(false)
                  }
                }}>
          Save grant
        </button>
        <span style={helpStyle}>
          With every right cleared, saving REMOVES this principal's grant at
          this scope.
        </span>
      </div>
    </div>
  )
}

// ── Operations ─────────────────────────────────────────────────────────────

function OperationsTab({ project, objName }: { project: string; objName: string }) {
  const [status, setStatus] = useState<DmsProviderStatus | null>(null)
  const [jobs, setJobs] = useState<Record<string, any>[]>([])
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [importPath, setImportPath] = useState('')
  const [busy, setBusy] = useState(false)
  const [reindex, setReindex] = useState<DmsReindexStatus | null>(null)
  const [confirmingReindex, setConfirmingReindex] = useState(false)

  const refresh = useCallback(async () => {
    try {
      const s = await providerStatus(project, objName)
      setStatus({ storage: s.storage, malware: s.malware, jobs: s.jobs,
                  embedding: s.embedding })
    } catch {
      setStatus(null)
    }
    try {
      const j = await listJobs(project, objName)
      setJobs(j.jobs || [])
      setCounts(j.counts || {})
    } catch {
      setJobs([])
    }
    try {
      // Also catches a run ANOTHER user started: the button disables for
      // everyone while the queue is non-empty, not just for the clicker.
      setReindex(await reindexStatus(project, objName))
    } catch {
      setReindex(null)
    }
  }, [project, objName])

  useEffect(() => { void refresh() }, [refresh])

  // While a re-index runs, poll its progress (and the queue panel) until the
  // pending+running count reaches zero.
  useEffect(() => {
    if (!reindex?.active) return
    const timer = window.setInterval(async () => {
      try {
        const next = await reindexStatus(project, objName)
        setReindex(next)
        if (!next.active) void refresh()
      } catch {
        // One missed poll is not a reason to stop showing progress.
      }
    }, 2500)
    return () => window.clearInterval(timer)
  }, [reindex?.active, project, objName, refresh])

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 860 }}>
      <Group title="Storage">
        {status ? (
          <p style={{ fontSize: TYPE.meta, margin: 0, lineHeight: 1.55 }}>
            <StatusChip tone={status.storage.ok ? 'ok' : 'bad'}>
              {status.storage.ok ? 'Reachable' : 'Not reachable'}
            </StatusChip>{' '}
            {status.storage.detail}
            <br />
            <span style={helpStyle}>
              Capabilities: {status.storage.capabilities.join(', ') || 'none reported'}
            </span>
          </p>
        ) : <p style={helpStyle}>Status unavailable.</p>}
      </Group>

      <Group title="Virus checking">
        {status ? (
          <p style={{ fontSize: TYPE.meta, margin: 0, lineHeight: 1.55 }}>
            {status.malware.configured
              ? `${status.malware.backend}: ${status.malware.detail}`
              : 'No scanner configured. Uploads are recorded as "skipped" — which is honest, but it means the validate stage is not a threat check.'}
            {status.malware.required && !status.malware.configured ? (
              <><br /><strong style={{ color: 'var(--color-error)' }}>
                This repository requires a scan, so uploads will be refused
                until one is configured.
              </strong></>
            ) : null}
          </p>
        ) : null}
      </Group>

      <Group title="Answering questions"
             note="Four things have to line up before a question is answered from vectors: a resolvable embedding profile, a database that can hold vectors, an index over them, and chunks that have actually been embedded. Each fails quietly on its own, so each is reported separately.">
        {status?.embedding ? (() => {
          const e = status.embedding
          const line = (ok: boolean, text: React.ReactNode) => (
            <p style={{ fontSize: TYPE.meta, margin: '3px 0', lineHeight: 1.55 }}>
              <span style={{ color: ok
                ? 'var(--color-success)'
                : 'var(--color-warning)' }}>{ok ? '●' : '○'}</span>{' '}
              {text}
            </p>
          )
          return (
            <>
              {line(e.backend === 'pgvector' && e.column,
                e.backend !== 'pgvector'
                  ? 'This database has no pgvector, so retrieval matches by keyword. Chunks, rights and redaction all still work.'
                  : e.column
                    ? 'The chunk table holds vectors (pgvector).'
                    : 'pgvector is available but the chunk table has no embedding column yet — it is added on the first embedded ingestion.')}
              {e.column && line(e.indexed,
                e.indexed
                  ? 'Nearest-neighbour index present.'
                  : 'No vector index yet — every question scans every chunk. It is created with the embedding column; re-run an ingestion job to add it.')}
              {line(e.chunks > 0 && e.pending === 0,
                e.chunks === 0
                  ? 'No chunks indexed yet — upload a document, or run the queue below.'
                  : e.pending > 0
                    ? `${e.embedded} of ${e.chunks} chunks embedded — ${e.pending} still pending (an unset embedding profile, or a provider error; the queue below shows which).`
                    : `${e.chunks} chunks embedded${e.model ? ` with ${e.model}` : ''}${e.dims ? ` (${e.dims} dimensions)` : ''}.`)}
            </>
          )
        })() : <p style={helpStyle}>Embedding status unavailable.</p>}
        <div style={{ display: 'flex', gap: 10, marginTop: 8, alignItems: 'center' }}>
          <button type="button" style={btnStyle} disabled={busy || Boolean(reindex?.active)}
                  onClick={() => setConfirmingReindex(true)}>
            {reindex?.active ? 'Re-indexing…' : 'Re-index documents…'}
          </button>
          {confirmingReindex ? (
            <ReindexConfirm
              loadPending={() => reindexPending(project, objName)}
              onClose={() => setConfirmingReindex(false)}
              onRun={async (mode) => {
                setConfirmingReindex(false)
                setBusy(true)
                try {
                  const res = await reindexRepository(project, objName, '', mode)
                  setReindex(res)
                  toast.success(`Re-indexing ${res.queued} document${res.queued === 1 ? '' : 's'} in the background`)
                } catch (reindexError) {
                  toast.error(reindexError instanceof Error
                    ? reindexError.message : 'Re-index failed')
                  await refresh()
                } finally { setBusy(false) }
              }}
            />
          ) : null}
          {reindex?.active ? (
            <span style={{ fontSize: TYPE.meta, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <progress value={reindex.done + reindex.parked} max={reindex.total || 1}
                        style={{ width: 160 }} />
              {reindex.done + reindex.parked} of {reindex.total}
              {reindex.parked ? ` (${reindex.parked} blocked)` : ''}
            </span>
          ) : reindex && reindex.total > 0 ? (
            <span style={helpStyle}>
              Last run: {reindex.done} re-indexed
              {reindex.parked ? `, ${reindex.parked} blocked (see the queue below)` : ''}.
            </span>
          ) : null}
        </div>
        <p style={helpStyle}>
          Re-runs extraction, chunking and embedding in the background. You
          will see how many documents are actually out of date first, and can
          re-index just those — a full run re-embeds the whole repository and
          needs a second confirmation. Use it after configuring or changing
          the embedding profile — chunks written before a profile existed
          carry no vectors, and nothing revisits them on its own. Only one
          re-index runs per repository, whoever starts it.
        </p>
      </Group>

      <Group title="Work in progress"
             note="Extraction, OCR and indexing run as durable jobs with exponential backoff. A job that fails eight times is parked rather than retried forever, and shows here with its error.">
        <JobQueue jobs={jobs} counts={counts} busy={busy}
                  onRunNow={async () => {
                    setBusy(true)
                    try {
                      const res = await drainJobs(project, objName)
                      toast.success(`Ran ${res.done} of ${res.claimed}`)
                      await refresh()
                    } finally { setBusy(false) }
                  }}
                  onRetry={async (jobId) => {
                    await retryJob(project, objName, jobId)
                    await refresh()
                  }}
                  onRefresh={() => void refresh()} />
      </Group>

      <Group title="Bulk import"
             note="Walks a server folder or an S3 prefix and queues every file. Idempotent on content hash, so re-running does not create duplicates, and each batch reports its own per-file errors.">
        <div style={fieldRowStyle}>
          <input style={{ ...inputStyle, flex: 1 }} value={importPath}
                 placeholder="/srv/corpus/documents  or  s3-prefix/"
                 onChange={(e) => setImportPath(e.target.value)} />
          <button type="button" style={btnStyle} disabled={!importPath || busy}
                  onClick={async () => {
                    setBusy(true)
                    try {
                      const res = await bulkImport(project, objName, {
                        source: importPath.startsWith('/') ? 'local' : 's3',
                        path: importPath,
                      })
                      toast.success(res.detail)
                      await refresh()
                    } catch (importError) {
                      toast.error(importError instanceof Error
                        ? importError.message : 'Import failed')
                    } finally { setBusy(false) }
                  }}>
            Queue import
          </button>
        </div>
      </Group>
    </div>
  )
}

// What each job kind actually does to a document — "ingest" is jargon.
const JOB_KIND_LABELS: Record<string, string> = {
  ingest: 'Index',
  reindex: 'Re-index',
  import: 'Import batch',
  retention: 'Apply retention',
  merge: 'Build merged document',
  purge: 'Remove from search index',
}

const JOB_STATUS_TONE: Record<string, string> = {
  parked: 'var(--color-error)',
  running: 'var(--color-accent, #2b6cb0)',
  pending: 'var(--color-warning)',
  done: 'var(--color-success)',
}

function jobStatusLabel(job: Record<string, any>): string {
  const status = String(job.status)
  const attempts = Number(job.attempts ?? 0)
  if (status === 'parked') return 'failed — gave up'
  if (status === 'pending') {
    return attempts > 0 ? `failed — will retry (attempt ${attempts} of 8)` : 'waiting'
  }
  if (status === 'running') return 'running'
  if (status === 'done') return 'done'
  return status
}

function formatJobWhen(value: unknown): string {
  if (!value) return ''
  const date = new Date(String(value))
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

/**
 * The queue, worth reading: each row names its DOCUMENT, problems and active
 * work come first, and the pile of finished jobs sits behind a toggle — a
 * list of forty "ingest · done · 0" rows told nobody anything.
 */
function JobQueue({ jobs, counts, busy, onRunNow, onRetry, onRefresh }: {
  jobs: Record<string, any>[]
  counts: Record<string, number>
  busy: boolean
  onRunNow: () => Promise<void>
  onRetry: (jobId: string) => Promise<void>
  onRefresh: () => void
}) {
  const [showDone, setShowDone] = useState(false)
  const weight: Record<string, number> = { parked: 0, running: 1, pending: 2 }
  const attention = jobs
    .filter((j) => String(j.status) !== 'done')
    .sort((a, b) => (weight[String(a.status)] ?? 3) - (weight[String(b.status)] ?? 3))
  const completed = jobs.filter((j) => String(j.status) === 'done')
  const doneCount = counts.done ?? completed.length
  const summary = [
    counts.parked ? `${counts.parked} failed` : '',
    counts.running ? `${counts.running} running` : '',
    counts.pending ? `${counts.pending} waiting` : '',
    doneCount ? `${doneCount} completed` : '',
  ].filter(Boolean).join(' · ')
  const visible = (showDone ? [...attention, ...completed] : attention).slice(0, 30)
  const actionable = attention.length > 0

  return (
    <>
      <p style={{ fontSize: TYPE.meta, margin: 0, lineHeight: 1.55 }}>
        {summary || 'Nothing has been queued yet.'}
      </p>
      <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
        {(counts.pending || 0) > 0 ? (
          <button type="button" style={btnStyle} disabled={busy} onClick={() => void onRunNow()}
                  title="Waiting jobs run on their own within about 20 seconds — this just doesn't make you wait">
            Run waiting jobs now
          </button>
        ) : null}
        <button type="button" style={btnStyle} onClick={onRefresh}>Refresh</button>
        {doneCount > 0 ? (
          <button type="button"
                  style={{ ...btnStyle, border: 'none', background: 'none',
                           textDecoration: 'underline' }}
                  onClick={() => setShowDone((v) => !v)}>
            {showDone ? 'Hide completed' : `Show ${doneCount} completed`}
          </button>
        ) : null}
      </div>
      {!actionable && !showDone ? (
        <p style={{ ...helpStyle, marginTop: 8 }}>
          Nothing is waiting or failed
          {doneCount ? ' — every queued job has completed.' : '.'}
        </p>
      ) : null}
      {visible.length > 0 ? (
        <table style={{ ...tableStyle, marginTop: 10 }}>
          <thead><tr><Th>Document</Th><Th>Task</Th><Th>Status</Th><Th>When</Th><Th /></tr></thead>
          <tbody>
            {visible.map((job) => {
              const status = String(job.status)
              return (
                <tr key={String(job.id)} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ ...tdStyle, maxWidth: 260 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap' }}
                         title={String(job.document_title || '')}>
                      {String(job.document_title || 'Untitled')}
                    </div>
                    {job.last_error && status !== 'done' ? (
                      <div style={{ fontSize: TYPE.small, marginTop: 2,
                                    color: 'var(--color-error)',
                                    whiteSpace: 'normal' }}>
                        {String(job.last_error)}
                      </div>
                    ) : null}
                  </td>
                  <td style={tdStyle}>{JOB_KIND_LABELS[String(job.kind)] || String(job.kind)}</td>
                  <td style={{ ...tdStyle, color: JOB_STATUS_TONE[status] || 'inherit' }}>
                    {jobStatusLabel(job)}
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--color-text-secondary)', fontSize: TYPE.small }}>
                    {formatJobWhen(job.processed_at || job.created_at)}
                  </td>
                  <td style={tdStyle}>
                    {status === 'parked' ? (
                      <button type="button" style={btnStyle} disabled={busy}
                              title="Puts the job back in the queue with a fresh attempt counter — do this after fixing what made it fail"
                              onClick={() => void onRetry(String(job.id))}>
                        Retry
                      </button>
                    ) : null}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      ) : null}
    </>
  )
}

// ── small shared bits ──────────────────────────────────────────────────────

function Field({ label, help, children }: {
  label: string; help?: string; children: React.ReactNode
}) {
  return (
    <label style={{ display: 'block' }}>
      <span style={dmsLabelStyle}>{label}</span>
      {children}
      {help ? <span style={helpStyle}>{help}</span> : null}
    </label>
  )
}

function Toggle({ label, help, checked, onChange }: {
  label: string; help?: string; checked: boolean; onChange: (value: boolean) => void
}) {
  return (
    <div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: TYPE.meta }}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        {label}
      </label>
      {help ? <span style={helpStyle}>{help}</span> : null}
    </div>
  )
}

function Group({ title, note, children }: {
  title: string; note?: string; children: React.ReactNode
}) {
  return (
    <section style={panelStyle}>
      <h3 style={groupTitleStyle}>{title}</h3>
      {note ? <p style={helpStyle}>{note}</p> : null}
      <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>{children}</div>
    </section>
  )
}

function Th({ children }: { children?: React.ReactNode }) {
  return (
    <th style={{ textAlign: 'left', padding: '9px 10px', fontSize: TYPE.small,
                 fontWeight: 600, color: T.textFaint, whiteSpace: 'nowrap' }}>
      {children}
    </th>
  )
}

/* ── the shared document vocabulary ──────────────────────────────────────
   Everything below is `@twasta/page-runtime/dms/styles`, so the designer and
   the screens it configures read as one product: the same type scale (15/14/13,
   no 11px), the same 8px radii, the same `.btn` family, the same chips. They
   are kept as local names because ~200 call sites in this file already use
   them — rebasing the definitions changes every screen at once without
   touching any of them. */

const headerStyle: React.CSSProperties = dmsHeaderStyle

const btnStyle: React.CSSProperties = dmsBtnStyle

const listBtnStyle: React.CSSProperties = {
  display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px',
  border: 'none', borderRadius: RADIUS.md, fontSize: TYPE.meta,
  fontFamily: 'inherit', cursor: 'pointer', color: T.text,
}

const inputStyle: React.CSSProperties = { ...dmsInputStyle, width: '100%' }

/* Sentence case at reading size, matching `Section` in the run views. The
   uppercase 11px micro-caps this replaced were a different design language
   from the screens the designer configures. */
const groupTitleStyle: React.CSSProperties = {
  margin: 0, fontSize: TYPE.heading, fontWeight: 600, color: T.text,
}

const helpStyle: React.CSSProperties = {
  display: 'block', fontSize: TYPE.small, color: T.textSecondary,
  marginTop: 5, lineHeight: 1.55,
}

/** Two settings on one field that cancel out. Amber and inline, beside the
 *  buttons that produced them. */
const conflictStyle: React.CSSProperties = {
  width: '100%',
  margin: '4px 0 0',
  padding: '6px 9px',
  borderRadius: RADIUS.md,
  background: T.warningWeak,
  color: T.warning,
  fontSize: TYPE.small,
  lineHeight: 1.5,
}

/** Setup that is not finished. Amber, above whichever tab is open. */
const setupWarnStyle: React.CSSProperties = {
  marginBottom: 16,
  padding: '12px 14px',
  borderRadius: RADIUS.lg,
  border: `1px solid ${T.warning}`,
  background: T.warningWeak,
  color: T.warning,
  fontSize: TYPE.meta,
  lineHeight: 1.55,
}

/** The "in plain words" box under a rights row. */
const plainWordsStyle: React.CSSProperties = {
  padding: '11px 13px',
  borderRadius: RADIUS.md,
  background: T.surface2,
  border: `1px solid ${T.border}`,
  fontSize: TYPE.meta,
  lineHeight: 1.55,
  color: T.textSecondary,
}

/** A bordered panel, same as a card in the run views. */
const panelStyle: React.CSSProperties = dmsCardStyle

const fieldRowStyle: React.CSSProperties = {
  display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
}

/* A field is no longer one line — its default, its id rules and its sensitive
   options are all part of the same decision, so they are grouped visibly
   rather than left to run together with the next field's key. */
const fieldCardStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 6,
  padding: '10px 12px', marginBottom: 8,
  border: `1px solid ${T.border}`, borderRadius: RADIUS.md,
  background: T.surface2,
}

const checkLabelStyle: React.CSSProperties = {
  display: 'flex', gap: 6, alignItems: 'center', fontSize: TYPE.small,
  color: T.textSecondary, whiteSpace: 'nowrap',
}

const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', fontSize: TYPE.small,
}

const tdStyle: React.CSSProperties = {
  padding: '9px 10px', verticalAlign: 'top', lineHeight: 1.5,
}

const errorBoxStyle: React.CSSProperties = {
  padding: 24, fontSize: TYPE.body, lineHeight: 1.55, color: T.error,
}

const mutedBoxStyle: React.CSSProperties = {
  padding: 24, fontSize: TYPE.body, color: T.textSecondary,
}
